import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { Loader } from "@package/subframe/components/Loader";
import { FeatherAlertCircle, FeatherCheck, FeatherClock } from "@subframe/core";
import { useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";

import { FollowUpActionResponseDto } from "@/generated/model";
import {
  type DashboardActionGroup,
  formatActionDueDate,
  formatActionDueDateTime,
  getActionStatusBadge,
  groupActionsForDashboard,
  isActionOverdue,
} from "@/lib/follow-up-action-utils";
import { NURSERY_ROLE_LABELS } from "@/lib/roles";

const SECTION_CONFIG: Record<
  DashboardActionGroup,
  {
    label: string;
    variant: "error" | "neutral" | "brand" | "success";
    icon?: React.ReactNode;
    emptyText: string;
  }
> = {
  late: {
    label: "Late",
    variant: "error",
    icon: <FeatherAlertCircle />,
    emptyText: "No late actions",
  },
  open: {
    label: "Open",
    variant: "neutral",
    emptyText: "No open actions",
  },
  pending: {
    label: "Pending Confirmation",
    variant: "brand",
    emptyText: "No actions pending confirmation",
  },
  complete: {
    label: "Complete (last 7 days)",
    variant: "success",
    icon: <FeatherCheck />,
    emptyText: "No recently completed actions",
  },
};

interface FollowUpActionsTabProps {
  actions: FollowUpActionResponseDto[];
  isLoading: boolean;
  sqNameMap: Map<string, string>;
}

export function FollowUpActionsTab({
  actions,
  isLoading,
  sqNameMap,
}: FollowUpActionsTabProps) {
  const navigate = useNavigate();

  const grouped = useMemo(() => groupActionsForDashboard(actions), [actions]);

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center py-8">
        <Loader size="medium" />
      </div>
    );
  }

  const totalCount =
    grouped.late.length +
    grouped.open.length +
    grouped.pending.length +
    grouped.complete.length;

  if (totalCount === 0) {
    return (
      <div className="flex w-full items-center justify-center py-8">
        <span className="text-body font-body text-subtext-color">
          No follow-up actions
        </span>
      </div>
    );
  }

  const sectionOrder: DashboardActionGroup[] = [
    "late",
    "open",
    "pending",
    "complete",
  ];

  return (
    <div className="w-full overflow-x-auto rounded-sm bg-white">
      {/* Table Header */}
      <div className="flex w-full flex-col items-start">
        <div
          className="flex w-full items-center gap-4 pl-4 pr-4 py-2"
          style={{ minWidth: 900 }}
        >
          <span className="grow shrink-0 basis-0 text-caption-bold font-caption-bold text-neutral-500">
            Description
          </span>
          <span className="w-36 flex-none text-caption-bold font-caption-bold text-neutral-500">
            Due
          </span>
          <span className="w-32 flex-none text-caption-bold font-caption-bold text-neutral-500">
            Assignee
          </span>
          <span className="w-24 flex-none text-caption-bold font-caption-bold text-neutral-500">
            Approval
          </span>
          <span className="w-32 flex-none text-caption-bold font-caption-bold text-neutral-500 text-center">
            Status
          </span>
          <span className="w-16 flex-none"></span>
        </div>
      </div>

      {/* Status-grouped sections */}
      {sectionOrder.map((group) => {
        const items = grouped[group];
        const config = SECTION_CONFIG[group];

        return (
          <div key={group}>
            {/* Section header */}
            <div
              className="flex w-full items-center gap-2 pl-4 pr-4 py-2 bg-neutral-50 border-t border-neutral-border"
              style={{ minWidth: 900 }}
            >
              <Badge variant={config.variant} icon={config.icon}>
                {config.label}
              </Badge>
              <span className="text-caption font-caption text-subtext-color">
                {items.length === 0
                  ? config.emptyText
                  : `${items.length} ${items.length === 1 ? "action" : "actions"}`}
              </span>
            </div>

            {/* Action rows */}
            {items.map((action, index) => {
              const sqId = action.scheduledQuestionnaireId;
              const checklistName = sqId ? sqNameMap.get(sqId) : undefined;
              const overdue = isActionOverdue(action);
              const dueDateText = formatActionDueDate(action);
              const dueDateTimeText = formatActionDueDateTime(action);

              return (
                <div className="w-full" key={action.id}>
                  <div
                    className="flex w-full items-center gap-4 rounded-sm pl-4 pr-4 py-3"
                    style={{ minWidth: 900 }}
                  >
                    {/* Description */}
                    <div
                      className="flex grow basis-0 min-w-0 flex-col items-start cursor-pointer"
                      onClick={() => {
                        if (sqId) {
                          navigate({
                            to: "/checklists/$id",
                            params: { id: sqId },
                          });
                        }
                      }}
                    >
                      <span className="text-body-bold font-body-bold text-neutral-900 w-full break-words">
                        {action.description}
                      </span>
                      <span className="text-caption font-caption text-subtext-color truncate w-full">
                        From: {checklistName || sqId?.slice(0, 8) || "—"}
                      </span>
                    </div>

                    {/* Due date & time */}
                    <div className="flex w-36 flex-none flex-col items-start gap-0.5">
                      {dueDateTimeText ? (
                        <>
                          <div className="flex items-center gap-1">
                            {!overdue && (
                              <FeatherClock className="h-3 w-3 text-subtext-color" />
                            )}
                            <span
                              className={`text-caption font-caption ${overdue ? "text-error-700 font-caption-bold" : "text-neutral-700"}`}
                            >
                              {dueDateTimeText}
                            </span>
                          </div>
                          {dueDateText && (
                            <span
                              className={`text-caption font-caption ${overdue ? "text-error-600" : "text-subtext-color"}`}
                            >
                              {dueDateText}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-caption font-caption text-subtext-color">
                          No due date
                        </span>
                      )}
                    </div>

                    {/* Assignee */}
                    <div className="flex w-32 flex-none items-center">
                      {action.assignee ? (
                        <span className="text-caption font-caption text-neutral-700 truncate">
                          {[action.assignee.firstName, action.assignee.lastName]
                            .filter(Boolean)
                            .join(" ") || action.assignee.email}
                        </span>
                      ) : action.assigneeRole ? (
                        <Badge variant="neutral">
                          {NURSERY_ROLE_LABELS[action.assigneeRole] ||
                            action.assigneeRole}
                        </Badge>
                      ) : (
                        <span className="text-caption font-caption text-subtext-color">
                          —
                        </span>
                      )}
                    </div>

                    {/* Approval */}
                    <div className="flex w-24 flex-none items-center">
                      {action.requiresApproval ? (
                        <Badge variant="warning">Required</Badge>
                      ) : (
                        <span className="text-caption font-caption text-subtext-color">
                          —
                        </span>
                      )}
                    </div>

                    {/* Status */}
                    <div className="flex w-32 flex-none items-center justify-center">
                      {getActionStatusBadge(action.status)}
                    </div>

                    {/* View button */}
                    <div className="flex w-16 flex-none items-center justify-end">
                      <Button
                        variant="neutral-primary"
                        onClick={() =>
                          navigate({
                            to: "/follow-up-actions/$actionId",
                            params: { actionId: action.id },
                          })
                        }
                      >
                        View
                      </Button>
                    </div>
                  </div>

                  {/* Divider between rows within a section */}
                  {index < items.length - 1 && (
                    <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
